import { connect } from "react-redux";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import EgresosActions from "../../../stores/hospitalizacion/egresos/Actions";
import CustomInput from "../../../components/CustomForm/CustomInput";
import CustomSelect from "../../../components/CustomForm/CustomSelect";
import CustomAutocomplete from "../../../components/CustomForm/CustomAutocomplete/CustomAutocomplete";
import CustomButton from "../../../components/CustomButton/CustomButton";
import styled from "styled-components";
import {
  FormWrapper,
  Form,
  FormGroup,
  LabelWithContainer,
} from "../../../components/CustomForm/FormStyles";
import Colors from "../../../themes/Colors";
import useEmpresa from "../../../hooks/useEmpresa";
import { useMountEffect } from "../../../hooks/LifeCycle";
import moment from "moment";
import { DATE_FORMATS, TIME_SCALE } from "../../../utils/Constant";
import { camposCompletos } from "../../../utils/ValidationUtil";
import { ALERTAS_GENERICAS } from "../../../utils/AlertasGenericas";
import { 
  MAXIMO_DIAS_CONSULTA_CIERRES,
  SERVICIO_AMBULATORIO,
  SERVICIO_HOSPITALIZACION,
  SERVICIO_EMERGENCIA,
  SERVICIO_MATERNIDAD,
  SERVICIO_PARTO,
  SERVICIO_CESAREA,
} from "../utils/HospitalizacionConstants";
import { buildSearchParams, buildMedicoOption, formatMedicoLabel } from "../utils/CoberturasMedicosUtils";

const DEBOUNCE_MEDICO_MS = 500;

const CompactFormWrapper = styled(FormWrapper)`
  padding: 15px;
  margin: 0;
`;

const BusquedaCierresCuentasForm = (props) => {
  const {
    formConsultaCierresCuentasData,
    consultarCierresCuentasLoading,
    getMedicos,
    getMedicosData,
    getMedicosLoading,
    getMedicosError,
    consultarPlanesEgresoData,
    consultarPlanesEgresoLoading,
  } = props;

  const { prestador } = props?.authUserData;
  const empresa = useEmpresa();
  const mostrarFiltroMedico = prestador?.esARS === "S";

  const [medicoSearchTerm, setMedicoSearchTerm] = useState("");

  const medicosOptions = useMemo(() => {
    if (!getMedicosData?.medicos || getMedicosLoading || getMedicosError) {
      return [];
    }
    return getMedicosData.medicos.map(buildMedicoOption);
  }, [getMedicosData?.medicos, getMedicosLoading, getMedicosError]);

  const planOptions = useMemo(() => {
    const planes = consultarPlanesEgresoData?.planes || [];
    const items = planes.map((p) => ({
      value: String(p.codigoPlan),
      label: p.descripcion || String(p.codigoPlan),
    }));
    return [{ value: "", label: "Todos" }, ...items];
  }, [consultarPlanesEgresoData?.planes]);

  useEffect(() => {
    if (!medicoSearchTerm || typeof medicoSearchTerm !== "string" || !medicoSearchTerm.trim()) {
      return;
    }
    const timer = setTimeout(() => {
      const searchParams = buildSearchParams(medicoSearchTerm.trim(), false);
      if (searchParams && getMedicos) {
        getMedicos(searchParams);
      }
    }, DEBOUNCE_MEDICO_MS);
    return () => clearTimeout(timer);
  }, [medicoSearchTerm, getMedicos]);

  const handleMedicoChange = useCallback(
    (value) => {
      setMedicoSearchTerm(value || "");
      if (!value || (typeof value === "string" && value.trim() === "")) {
        props.formConsultaCierresCuentas({
          ...formConsultaCierresCuentasData,
          medicoSeleccionado: null,
        });
      }
    },
    [formConsultaCierresCuentasData, props]
  );

  const handleMedicoSelect = useCallback(
    (option) => {
      setMedicoSearchTerm("");
      props.formConsultaCierresCuentas({
        ...formConsultaCierresCuentasData,
        medicoSeleccionado: option,
      });
    },
    [formConsultaCierresCuentasData, props]
  );

  useMountEffect(() => {
    let startDate = moment(new Date())
      .subtract(MAXIMO_DIAS_CONSULTA_CIERRES, TIME_SCALE.dia)
      .format(DATE_FORMATS.middledash_ymd);

    let today = moment(new Date()).format(DATE_FORMATS.middledash_ymd);

    consultarUltimosCierres(startDate, today);
  });

  const onStateChange =
    (key, format = null) =>
    (value) => {
      props.formConsultaCierresCuentas({
        ...formConsultaCierresCuentasData,
        [key]: format ? format(value) : value,
      });
    };

  const consultarUltimosCierres = (fechaInicio, fechaFin) => {
    props.consultarCierresCuentas({
      formConsultaCierresCuentasData: formConsultaCierresCuentasData,
      formHandler: props.formConsultaCierresCuentas,
      reclamante: prestador?.codigo,
      fechaInicio,
      fechaFin,
      numeroReclamo: undefined,
      numeroCarnet: undefined,
      servicio: undefined,
      fechaServicioDesde: undefined,
      fechaServicioHasta: undefined,
      cedula: undefined,
      plan: undefined,
      pssHonorario: undefined,
      numeroPagina: 1,
      tamanoPagina: 10,
    });
  };

  const buscarCierresCuentas = () => {
    const { 
      fechaInicio, 
      fechaFin,
      numeroReclamo,
      numeroCarnet,
      servicio,
      fechaServicioDesde,
      fechaServicioHasta,
      cedula,
      plan,
      medicoSeleccionado,
    } = formConsultaCierresCuentasData;

    let mensajeValidacion = null;

    if (!camposCompletos({ fechaInicio, fechaFin })) {
      mensajeValidacion = ALERTAS_GENERICAS.CAMPOS_REQUERIDOS;
    } else if (
      moment(fechaInicio)
        .startOf("day")
        .isAfter(moment(fechaFin).startOf("day"))
    ) {
      mensajeValidacion = ALERTAS_GENERICAS.FECHA_DESDE_HASTA_INVALIDA;
    }

    const rangoDias = moment(fechaFin).diff(moment(fechaInicio), "days");

    if (rangoDias > MAXIMO_DIAS_CONSULTA_CIERRES) {
      mensajeValidacion = `El rango de fechas debe ser menor de ${MAXIMO_DIAS_CONSULTA_CIERRES} días.`;
    }

    if (mensajeValidacion) {
      props.formConsultaCierresCuentas({
        ...formConsultaCierresCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: mensajeValidacion,
          onConfirm: () => {
            props.formConsultaCierresCuentas({
              ...formConsultaCierresCuentasData,
              showAlert: false,
            });
          },
        },
        showAlert: true,
      });
      return;
    }

    // Preparar filtros: solo enviar si tienen valor
    const filtros = {
      numeroReclamo: numeroReclamo?.trim() || undefined,
      numeroCarnet: numeroCarnet?.trim() || undefined,
      servicio: servicio?.trim() || undefined,
      fechaServicioDesde: fechaServicioDesde?.trim() || undefined,
      fechaServicioHasta: fechaServicioHasta?.trim() || undefined,
      cedula: cedula?.trim() || undefined,
      plan: plan?.trim() || undefined,
    };

    const pssHonorario = medicoSeleccionado?.codigo
      ? String(medicoSeleccionado.codigo).trim()
      : undefined;

    props.consultarCierresCuentas({
      formConsultaCierresCuentasData: formConsultaCierresCuentasData,
      formHandler: props.formConsultaCierresCuentas,
      reclamante: prestador?.codigo,
      fechaInicio,
      fechaFin,
      numeroReclamo: filtros.numeroReclamo,
      numeroCarnet: filtros.numeroCarnet,
      servicio: filtros.servicio,
      fechaServicioDesde: filtros.fechaServicioDesde,
      fechaServicioHasta: filtros.fechaServicioHasta,
      cedula: filtros.cedula,
      plan: filtros.plan,
      pssHonorario: pssHonorario ? Number(pssHonorario) : undefined,
      numeroPagina: 1,
      tamanoPagina: 10,
    });
  };

  return (
    <CompactFormWrapper borderColor={Colors.transparent}>
      <Form justifyContent={"space-between"}>
        <FormGroup>
          <CustomInput
            type={"text"}
            placeholder={`No. Reclamo`}
            label={"No. Reclamo"}
            isLabelWithContainer={true}
            labelWidth={"270px"}
            width={"250px"}
            value={formConsultaCierresCuentasData?.numeroReclamo}
            onChange={onStateChange("numeroReclamo")}
          />
        </FormGroup>
        <FormGroup>
          <CustomInput
            type={"text"}
            placeholder={`Nombre Asegurado`}
            label={"Nombre Asegurado"}
            isLabelWithContainer={true}
            labelWidth={"270px"}
            width={"250px"}
            value={formConsultaCierresCuentasData?.nombreAsegurado}
            onChange={onStateChange("nombreAsegurado")}
          />
        </FormGroup>
        <FormGroup>
          <CustomInput
            type={"text"}
            placeholder={"No.Carnet"}
            label={"No.Carnet"}
            isLabelWithContainer={true}
            labelWidth={"270px"}
            width={"250px"}
            value={formConsultaCierresCuentasData?.numeroCarnet}
            onChange={onStateChange("numeroCarnet")}
          />
        </FormGroup>
        <FormGroup>
          <CustomInput
            type={"text"}
            placeholder={"Cédula"}
            label={"Cédula"}
            isLabelWithContainer={true}
            labelWidth={"270px"}
            width={"250px"}
            value={formConsultaCierresCuentasData?.cedula}
            onChange={onStateChange("cedula")}
          />
        </FormGroup>
        <FormGroup>
          <div style={{ width: "270px" }}>
            <CustomSelect
              label={"Plan de Salud"}
              isLabelWithContainer={true}
              labelWidth={"270px"}
              width={"270px"}
              value={formConsultaCierresCuentasData?.plan ?? ""}
              onChange={onStateChange("plan")}
              options={planOptions}
              showDefaultOption={false}
            />
          </div>
        </FormGroup>
        <FormGroup>
          <div style={{ width: "270px" }}>
            <CustomSelect
              label={"Servicio"}
              isLabelWithContainer={true}
              labelWidth={"270px"}
              width={"270px"}
              value={formConsultaCierresCuentasData?.servicio}
              onChange={onStateChange("servicio")}
              options={[
                { value: "", label: "Todos" },
                { value: SERVICIO_AMBULATORIO, label: "Ambulatorio" },
                { value: SERVICIO_HOSPITALIZACION, label: "Hospitalización" },
                { value: SERVICIO_EMERGENCIA, label: "Emergencia" },
                { value: SERVICIO_MATERNIDAD, label: "Maternidad" },
                { value: SERVICIO_PARTO, label: "Parto" },
                { value: SERVICIO_CESAREA, label: "Cesárea" },
              ]}
            />
          </div>
        </FormGroup>
        {mostrarFiltroMedico && (
          <FormGroup>
            <div style={{ width: "270px" }}>
              <LabelWithContainer
                font={empresa?.LETRA_PRINCIPAL}
                labelWidth={"270px"}
                labelHeight={"50px"}
              >
                Médico
              </LabelWithContainer>
              <CustomAutocomplete
                label=""
                placeholder="Buscar por código o nombre..."
                value={formatMedicoLabel(formConsultaCierresCuentasData?.medicoSeleccionado)}
                onChange={handleMedicoChange}
                onSelect={handleMedicoSelect}
                options={medicosOptions}
                displayKey="label"
                valueKey="value"
                height="32px"
                fontSize="13px"
                isLoading={getMedicosLoading}
                loadingText="Buscando médicos..."
              />
            </div>
          </FormGroup>
        )}
        <FormGroup>
          <CustomInput
            type={"date"}
            maxDate={formConsultaCierresCuentasData?.fechaFin}
            required={true}
            placeholder={"Fecha Salida Desde"}
            label={"Fecha Salida Desde"}
            isLabelWithContainer={true}
            labelWidth={"270px"}
            width={"250px"}
            value={formConsultaCierresCuentasData?.fechaInicio}
            onChange={onStateChange("fechaInicio")}
          />
        </FormGroup>
        <FormGroup>
          <CustomInput
            type={"date"}
            minDate={formConsultaCierresCuentasData?.fechaInicio}
            required={true}
            placeholder={"Fecha Salida Hasta"}
            label={"Fecha Salida Hasta"}
            isLabelWithContainer={true}
            labelWidth={"270px"}
            width={"250px"}
            value={formConsultaCierresCuentasData?.fechaFin}
            onChange={onStateChange("fechaFin")}
          />
        </FormGroup>
        <FormGroup>
          <CustomInput
            type={"date"}
            maxDate={formConsultaCierresCuentasData?.fechaServicioHasta}
            required={false}
            placeholder={"Fecha Servicio Desde"}
            label={"Fecha Servicio Desde"}
            isLabelWithContainer={true}
            labelWidth={"270px"}
            width={"250px"}
            value={formConsultaCierresCuentasData?.fechaServicioDesde}
            onChange={onStateChange("fechaServicioDesde")}
          />
        </FormGroup>
        <FormGroup>
          <CustomInput
            type={"date"}
            minDate={formConsultaCierresCuentasData?.fechaServicioDesde}
            required={false}
            placeholder={"Fecha Servicio Hasta"}
            label={"Fecha Servicio Hasta"}
            isLabelWithContainer={true}
            labelWidth={"270px"}
            width={"250px"}
            value={formConsultaCierresCuentasData?.fechaServicioHasta}
            onChange={onStateChange("fechaServicioHasta")}
          />
        </FormGroup>
        <FormGroup>
          <CustomButton
            label={"Buscar"}
            onClick={() => buscarCierresCuentas()}
            isLoading={consultarCierresCuentasLoading}
            height={"46px"}
            width={"100px"}
          />
        </FormGroup>
      </Form>
    </CompactFormWrapper>
  );
};

const mapStateToProps = (state) => ({
  authUserData: state.auth.authUserData,
  authUserLoading: state.auth.authUserLoading,
  authUserError: state.auth.authUserError,

  formConsultaCierresCuentasData:
    state.egreso.formConsultaCierresCuentasData,
  formConsultaCierresCuentasLoading:
    state.egreso.formConsultaCierresCuentasLoading,

  consultarCierresCuentasLoading:
    state.egreso.consultarCierresCuentasLoading,

  consultarPlanesEgresoData: state.egreso.consultarPlanesEgresoData,
  consultarPlanesEgresoLoading: state.egreso.consultarPlanesEgresoLoading,
});

const mapDispatchToProps = {
  formConsultaCierresCuentas: EgresosActions.formConsultaCierresCuentas,
  consultarCierresCuentas: EgresosActions.consultarCierresCuentas,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(BusquedaCierresCuentasForm);
